import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../../../config/queryKeys'
import { updateTeamPlayerFinderRegistration } from '../../../../services/team/playerFinderSettingsService'
import type {
  TeamPlayerFinderPayload,
  TeamPlayerFinderUpdateBody,
} from '../types'
import {
  freshRegistrationsFromConflict,
  isPlayerFinderConflict,
} from '../utils/playerFinderApiError'

interface UseTeamPlayerFinderMutationsOptions {
  teamId: string
  /** Same key part the settings query uses, so a 409 can refresh it in place. */
  authUserId?: string | number | null
}

/**
 * SN-4709 × SN-8081 — save one registration's Player Finder settings.
 *
 * Invalidate, never optimistic: the server decides what a save really did
 * (visibility, positions ownership, the next revision). On success both trees
 * are invalidated — the Team Budget sheet shows the same switch
 * (`player_finder_recruiting`, `finder_advertised`), so a stale sheet would claim
 * an ad that is no longer running.
 *
 * A 409 carries the FRESH settings in its body; they are written straight into
 * the query cache so the card shows the current state beside the choice the rep
 * was about to save, without a second round trip. The error still propagates —
 * the card owns the conflict message.
 *
 * retry: 0, overriding the app-wide mutations.retry: 1 — turning recruiting on
 * publishes an ad, and a retried save would 409 on its own stale revision anyway.
 */
export const useTeamPlayerFinderMutations = ({
  teamId,
  authUserId,
}: UseTeamPlayerFinderMutationsOptions) => {
  'use memo'
  const queryClient = useQueryClient()

  const updateRegistration = useMutation<
    TeamPlayerFinderPayload,
    unknown,
    { teamRegistrationId: string; body: TeamPlayerFinderUpdateBody }
  >({
    mutationFn: ({ teamRegistrationId, body }) =>
      updateTeamPlayerFinderRegistration(teamId, teamRegistrationId, body),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.teamPlayerFinder.all,
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.teamBalance.all }),
      ]),
    onError: (error) => {
      const fresh = freshRegistrationsFromConflict(error)
      if (fresh !== null) {
        queryClient.setQueryData<TeamPlayerFinderPayload>(
          queryKeys.teamPlayerFinder.byTeam(teamId, authUserId),
          { registrations: fresh }
        )
        return
      }
      // A conflict without the fresh settings in its body: refetch them instead.
      if (isPlayerFinderConflict(error)) {
        return queryClient.invalidateQueries({
          queryKey: queryKeys.teamPlayerFinder.all,
        })
      }
    },
    retry: 0,
  })

  return { updateRegistration }
}

export default useTeamPlayerFinderMutations
